import {
  useLevelPoolsWithUserAmount,
  useUser,
  useUserTier,
} from '@trustpad/launchpad';
import { StakingV2Provider } from '@trustpad/launchpad/dist/staking/hooks/useStakingV2';
import { UserTierState } from '@trustpad/launchpad/dist/tiers/hooks/useUserTier';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { StakingSection } from 'src/features/tiers/components/StakingSection/StakingSection';
import { FailedModal } from '~common/components/FailedModal';
import { Popup } from '~common/components/Popup';
import { SuccessModal } from '~common/components/SuccessModal';
import { ConnectWalletButton } from '~common/ConnectButtons/ConnectWalletButton';
import { useModalProvider } from '~common/hooks/useModalProvider';
import { StandardCard } from '~common/UI';
import { levelPools } from '~config/levelPoolsConfig';
import { TPAD } from '~config/tokens';
import { TiersListCard } from 'src/features/tiers/components/TiersListCard';
import { StakingDurationSlider } from './components/StakingSection/StakingDurationSlider';
import { StakingPoolMainNumbers } from './components/StakingSection/StakingPoolMainNumbers';
import { StakingTitleWithHelp } from './components/StakingSection/StakingTitleWithHelp';
import { UserStakingOverview } from './components/UserStakingOverview';
import { UserTierPerksOverview } from './components/UserTierPerksOverview';
import { useMaxUserPool } from './useMaxUserPool';
import { WalletNotification } from '~features/account/UserAccountPage'

export function TiersPage() {
  const { account, refresh: refreshUser } = useUser();
  const { message, onModalClose } = useModalProvider();

  const { tierState, refresh: refreshUserTier, isLoading } = useUserTier();
  const { maxPool, poolsWithAmounts } = useMaxUserPool();

  const refreshAll = useCallback(() => {
    refreshUser();
    refreshUserTier();
  }, [refreshUser, refreshUserTier]);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <WalletNotification account={account} />
        <StandardCard cardClassName="bg-opacity-90">
          {isLoading ? (
            account ? (
              <Skeleton height="2rem" />
            ) : (
              <div className="text-center flex flex-col justify-center h-full">
                <p className="text-gray-100 leading-relaxed text-lg mb-4">
                  Connect your wallet to stake your TPAD, reach new staking
                  levels and participate in IDOs!
                </p>
                <ConnectWalletButton />
              </div>
            )
          ) : (
            <UserStakingOverview
              tierState={tierState}
              poolsWithAmounts={poolsWithAmounts}
            />
          )}
        </StandardCard>

        <StandardCard cardClassName="bg-opacity-90">
          <h3 className="mb-3 text-gray-100">Your perks</h3>

          <UserTierPerksOverview
            tierState={tierState}
            maxPoolId={maxPool?.id}
          />
        </StandardCard>

        <StakingCard refreshAll={refreshAll} maxPoolId={maxPool?.id} />

        <TiersListCard activeTierId={tierState.tierId} />
      </div>

      {message && message.success && (
        <SuccessModal message={message} onClose={onModalClose} />
      )}
      {message && !message.success && (
        <FailedModal message={message} onClose={onModalClose} />
      )}
    </>
  );
}

function StakingCard({
  refreshAll,
  maxPoolId,
}: {
  refreshAll: VoidFunction;
  maxPoolId: string;
}) {
  const [stakingId, setStakingId] = useState('180 days');
  const [initialized, setInitialized] = useState(false);
  const activeStakingConfig = levelPools.find((i) => i.id === stakingId);

  useEffect(() => {
    if (!initialized && levelPools.map((i) => i.id).includes(maxPoolId)) {
      setStakingId(maxPoolId);
      setInitialized(true);
    }
  }, [maxPoolId, initialized]);

  return (
    <StandardCard className="space-y-6 pb-0" cardClassName="bg-opacity-90">
      <StakingTitleWithHelp />

      <StakingDurationSlider
        activeId={stakingId}
        onChange={setStakingId}
        highlightLastN={4}
      />

      <div>
        {activeStakingConfig.apy > 0 && (
          <div className="text-center mb-3">
            <span className="text-4xl md:text-5xl brand-text">
              {activeStakingConfig.apy}% APY
            </span>
            <Popup header="APY - Annual Percentage Yield">
              <p>
                The APY is dynamic with an emission rate that can be lower or
                higher than stated, depending on network activity that is
                continually updated and annualized. It is dynamic and not fixed
                because the APY includes the TPAD from staking fees, emissions,
                etc.
              </p>
              <p>
                All figures are estimates provided for your convenience only,
                and by no means represent guaranteed returns.
              </p>
            </Popup>
          </div>
        )}

        <StakingPoolMainNumbers stakingConfig={activeStakingConfig} />
      </div>

      <StakingV2Provider
        key={activeStakingConfig.address}
        address={activeStakingConfig.address}
        stakingToken={TPAD}
        rewardToken={TPAD}>
        {activeStakingConfig && (
          <StakingSection
            stakingConfig={activeStakingConfig}
            refresh={refreshAll}
          />
        )}
      </StakingV2Provider>
    </StandardCard>
  );
}
